import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Mail, ArrowLeft, Loader2, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;

    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: email.trim() });
      setSent(true);
      toast.success('Email envoyé si le compte existe');
    } catch (error) {
      toast.error('Erreur lors de l\'envoi');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="min-h-screen bg-[#050505] flex items-center justify-center p-6"
      style={{
        backgroundImage: `linear-gradient(to right, rgba(5,5,5,0.95), rgba(5,5,5,0.8)), url('https://images.unsplash.com/photo-1639323251610-d8218455e596?w=1200')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}
    >
      <div className="glass rounded-2xl p-8 max-w-md w-full">
        {/* Logo */}
        <div className="text-center mb-8">
          <img 
            src="/assets/trackvibes-logo.png" 
            alt="TrackVibes" 
            className="h-16 w-auto mx-auto mb-4"
          />
          <h1 className="font-heading text-2xl text-white">Mot de passe oublié</h1>
          <p className="text-gray-400 text-sm mt-2">
            Entre ton email pour recevoir un lien de réinitialisation
          </p>
        </div>

        {!sent ? (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label className="text-gray-300 flex items-center gap-2">
                <Mail className="w-4 h-4 text-neon-cyan" />
                Email
              </Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="votre@email.com"
                className="bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-neon-cyan h-12"
                disabled={loading}
                required
              />
            </div>

            <Button
              type="submit"
              disabled={loading || !email.trim()}
              className="w-full bg-neon-cyan text-black font-bold uppercase tracking-widest hover:shadow-[0_0_30px_rgba(0,240,255,0.6)] rounded-full py-6"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Envoi...
                </span>
              ) : (
                'Envoyer le lien'
              )}
            </Button>
          </form>
        ) : (
          <div className="text-center">
            <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-12 h-12 text-green-500" />
            </div>
            <h2 className="font-heading text-xl text-white mb-2">Email envoyé !</h2>
            <p className="text-gray-400 mb-6">
              Si un compte existe avec l'adresse <span className="text-neon-cyan">{email}</span>, 
              tu recevras un lien de réinitialisation.
            </p>
            <p className="text-gray-500 text-sm mb-4">
              Vérifie aussi tes spams si tu ne vois pas l'email.
            </p>
            <Button
              onClick={() => setSent(false)}
              variant="outline"
              className="border-white/20 text-gray-300 hover:bg-white/10 rounded-full"
            >
              Renvoyer l'email
            </Button>
          </div>
        )}

        <div className="mt-8 text-center">
          <Link 
            to="/login" 
            className="text-gray-400 hover:text-white flex items-center justify-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour à la connexion
          </Link>
        </div>
      </div>
    </div>
  );
}
